import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import * as yup from 'yup'
import { IModelBaseModel } from '../model-base/model-base.interface'
import { ModelBaseModel } from '../model-base/model-base.model'
import { IEventScheduleInterface, TCustomSchedule } from './event-scheduling.interface'

export class EventSchedulingModel
  extends ModelBaseModel<IEventScheduleInterface>
  implements IModelBaseModel
{
  public v: number
  public id: string | null
  public owner: string | null
  public title: string
  public briefDescription: string | null
  public created: Date
  public modified: Date
  public custom: TCustomSchedule[]
  public facilities: any[]
  public scheduleBy: 'class' | 'rider'
  public riderGroupPublish: IEventScheduleInterface['riderGroupPublish']
  public classGroupPublish: IEventScheduleInterface['classGroupPublish']

  public constructor(obj?: IEventScheduleInterface) {
    super()
    // default scheduleBy to class when it doesn't exist -- backwards compatibility for change in schema
    this.scheduleBy = obj?.scheduleBy ?? 'class'
    this.riderGroupPublish = obj?.riderGroupPublish ?? {}
    this.classGroupPublish = obj?.classGroupPublish ?? {}
    this.v = obj?.v ?? 1
    this.id = obj?.id ?? null
    this.owner = obj?.owner ?? null
    this.title = obj?.title ?? 'Sample Title'
    this.briefDescription = obj?.briefDescription ?? 'Sample brief description'
    this.custom =
      obj?.custom && obj?.custom.length > 0
        ? obj?.custom.map((custom) => ({
            ...custom,
            startDate: this.utcTimestamp({
              key: 'startDate' as any,
              isTimestamp: true,
              value: custom?.startDate,
            }),
            startTimeHours: custom?.startTimeHours ?? '00',
            startTimeMinutes: custom?.startTimeMinutes ?? '00',
            startTimeFormat: custom?.startTimeFormat ?? 'AM',
          }))
        : []
    this.facilities = obj?.facilities ?? [
      {
        facility: '',
        uuid: uuidv4(),
      },
    ]

    this.created = this.utcTimestamp({
      key: 'created',
      isTimestamp: true,
      value: obj?.created,
    })

    this.modified = this.utcTimestamp({
      key: 'modified',
      isTimestamp: true,
      changeOnUpdate: true,
      value: obj?.modified,
    })
  }

  static fromFirestoreDoc(doc: any) {
    return new EventSchedulingModel({
      id: doc.id,
      ...doc.data(),
    })
  }

  static validationSchema() {
    return yup.object().shape({
      scheduleBy: yup
        .string()
        .oneOf(['class', 'rider'], 'Schedule by must be either class or rider')
        .required('Schedule by is a required'),
      facilities: yup
        .array()
        .of(
          yup.object().shape({
            facility: yup.string().required('Facility is a required'),
          })
        )
        .test('uniqueValues', 'Facilities should be unique', (facilities) => {
          if (!facilities || !facilities?.length) return true
          let uniquesValuesSet = new Set(...[facilities?.map((c) => c?.facility)])
          return uniquesValuesSet.size === facilities?.length
        }),
      custom: yup.array().of(
        yup.object().shape({
          startDate: yup
            .date()
            .nullable()
            .transform((value, originalValue) => {
              if (!moment(originalValue).isValid()) return null
              return value
            })
            .typeError('Entered date must be valid')
            .required('Day is a required'),
          location: yup
            .string()
            .transform((value) => {
              return value.trim()
            })
            .required('Location is a required'),
          name: yup
            .string()
            .transform((value) => {
              return value.trim()
            })
            .required('Name is a required'),
          tag: yup
            .string()
            .transform((value) => {
              return value.trim()
            })
            .required('Tag is a required'),
        })
      ),
    })
  }
}
